/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationRemoteSessionIssuerMigratePreflightRequest,
  GetOrganizationRemoteSessionIssuerMigratePreflightSecurity,
} from "../models/operations/getorganizationremotesessionissuermigratepreflight.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionIssuerMigratePreflightQuery,
  OrganizationRemoteSessionIssuerMigratePreflightQueryData,
  prefetchOrganizationRemoteSessionIssuerMigratePreflight,
  queryKeyOrganizationRemoteSessionIssuerMigratePreflight,
} from "./organizationRemoteSessionIssuerMigratePreflight.core.js";
export {
  buildOrganizationRemoteSessionIssuerMigratePreflightQuery,
  type OrganizationRemoteSessionIssuerMigratePreflightQueryData,
  prefetchOrganizationRemoteSessionIssuerMigratePreflight,
  queryKeyOrganizationRemoteSessionIssuerMigratePreflight,
};

export type OrganizationRemoteSessionIssuerMigratePreflightQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getIssuerMigratePreflight organizationRemoteSessionIssuers
 *
 * @remarks
 * Authoritative impact summary for migrating a remote_session_issuer's clients onto another issuer: the clients that would move, the affected MCP servers, and every blocker (endpoint mismatches, conflicting MCP-server bindings). Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerMigratePreflight(
  request: GetOrganizationRemoteSessionIssuerMigratePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionIssuerMigratePreflightSecurity
    | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionIssuerMigratePreflightQueryData,
    OrganizationRemoteSessionIssuerMigratePreflightQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionIssuerMigratePreflightQueryData,
  OrganizationRemoteSessionIssuerMigratePreflightQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionIssuerMigratePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getIssuerMigratePreflight organizationRemoteSessionIssuers
 *
 * @remarks
 * Authoritative impact summary for migrating a remote_session_issuer's clients onto another issuer: the clients that would move, the affected MCP servers, and every blocker (endpoint mismatches, conflicting MCP-server bindings). Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerMigratePreflightSuspense(
  request: GetOrganizationRemoteSessionIssuerMigratePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionIssuerMigratePreflightSecurity
    | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionIssuerMigratePreflightQueryData,
    OrganizationRemoteSessionIssuerMigratePreflightQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionIssuerMigratePreflightQueryData,
  OrganizationRemoteSessionIssuerMigratePreflightQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionIssuerMigratePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationRemoteSessionIssuerMigratePreflightData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      sourceId: string;
      targetId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionIssuerMigratePreflightQueryData,
): OrganizationRemoteSessionIssuerMigratePreflightQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionIssuerMigratePreflight(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationRemoteSessionIssuerMigratePreflightQueryData
  >(key, data);
}

export function invalidateOrganizationRemoteSessionIssuerMigratePreflight(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      sourceId: string;
      targetId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "getMigratePreflight",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionIssuerMigratePreflight(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "getMigratePreflight",
    ],
  });
}
